import { WebGLRenderTarget } from "three";
import { devectorizeRGB } from "../helpers/vectorize";

class Raycaster {
  constructor(stageScene) {
    this.stageScene = stageScene;
    this.renderTarget = new WebGLRenderTarget(1, 1);
    this.buffer = new Uint8ClampedArray(1, 1);
    this.shrinkFactor = 2;
    this.prepareDebug(false);
  }

  dispose() {
    this.buffer = null;
    this.renderTarget.dispose();
    if (document.getElementById("raycaster")) {
      document.getElementById("raycaster").remove();
    }
  }

  setSize(w, h) {
    const offset = this.stageScene?.topOffset;
    this.width = Math.floor(w / this.shrinkFactor);
    this.height = Math.floor((h - offset) / this.shrinkFactor);

    if (this.height > 0 && this.width > 0) {
      this.renderTarget.setSize(this.width, this.height);
      this.buffer = new Uint8ClampedArray(this.width * this.height * 4);
      this.updateDebug();
    }
  }

  render(meshes, scene, renderer, camera) {
    if (!this.stageScene || !this.renderTarget || !renderer) return;
    meshes.forEach((m) => m.startRaycasting(scene, this.shrinkFactor));
    this.stageScene.setIsRaycasting(true);
    renderer.setRenderTarget(this.renderTarget);
    renderer.render(scene, camera);
    renderer.setRenderTarget(null);
    this.stageScene.setIsRaycasting(false);
    meshes.forEach((m) => m.stopRaycasting());
    this.extractPix(renderer);
    this.renderDebug();
  }

  extractPix(renderer) {
    const target = this.renderTarget;
    const w = this.width;
    const h = this.height;
    if (!(w > 0 && h > 0)) return;
    renderer.readRenderTargetPixels(target, 0, 0, w, h, this.buffer);
  }

  getPixRgbInd(ind) {
    const r = this.buffer[ind + 0];
    const g = this.buffer[ind + 1];
    const b = this.buffer[ind + 2];
    const a = this.buffer[ind + 3];
    if (a > 0 && a < 255) console.error("unstable a", a);
    return a === 255 ? devectorizeRGB([r, g, b]) : null;
  }

  raycast(meshes, x, y) {
    if (!this.buffer) return null;
    const x1 = Math.round(x / this.shrinkFactor);
    const y1 = Math.round(y / this.shrinkFactor);
    const itemInd = this.getPixRgbInd((y1 * this.width + x1) * 4);

    if (itemInd === null || itemInd < 1) return null;
    return meshes
      .map((m) => m.canRaycast() && m.getItemOnIndex(itemInd - 1))
      .filter(Boolean)
      .pop();
  }

  prepareDebug(isDebug) {
    if (isDebug) {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      if (document.getElementById("raycaster")) {
        document.getElementById("raycaster").remove();
      }
      document.body.appendChild(canvas);
      canvas.width = 1;
      canvas.style.width = `1px`;
      canvas.height = 1;
      canvas.style.height = `1px`;
      canvas.style.position = "fixed";
      canvas.style.bottom = "0px";
      canvas.style.right = "0px";
      canvas.style.transform = "scaleY(-1)";
      canvas.style.backgroundColor = "white";
      canvas.setAttribute("id", "raycaster");
      ctx.imageSmoothingEnabled = false;
      this.canvas = canvas;
      this.ctx = ctx;
    }
  }

  updateDebug() {
    if (this.ctx) {
      this.canvas.width = this.width;
      this.canvas.height = this.height;
      this.canvas.style.width = `${this.width}px`;
      this.canvas.style.height = `${this.height}px`;
    }
  }

  renderDebug() {
    if (this.ctx) {
      const dataImage = this.ctx.createImageData(this.width, this.height);
      dataImage.data.set(this.buffer);
      this.ctx.putImageData(dataImage, 0, 0);
    }
  }
}

export default Raycaster;
